import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = userInfo.name || 'Admin';
  const userInitials = userName.split(' ').map(name => name[0]).join('').toUpperCase();
  
  let title = 'Dashboard';
  let description = 'Overview of all metrics';

  // Set title and description based on current route
  if (location.pathname.startsWith('/reviews')) {
    title = 'Reviews Dashboard';
    description = 'Monitor and manage customer feedback';
  } else if (location.pathname.startsWith('/analytics')) {
    title = 'Analytics';
    description = 'In-depth data analysis and trends';
  } else if (location.pathname.startsWith('/insights')) {
    title = 'Insights';
    description = 'Key findings and recommendations';
  } else if (location.pathname.startsWith('/settings')) {
    title = 'Settings';
    description = 'Configure your account preferences';
  }

  // Handle clicks outside of dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    
    // Add event listener when dropdown is open
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Cleanup event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = () => {
    // Clear authentication status
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    
    // Redirect to login page
    navigate('/login');
  };

  return (
    <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
      <div>
        <h1 className="text-lg font-medium">{title}</h1>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <div className="flex items-center">
        <div className="mr-4 relative">
          <button className="text-gray-500 hover:text-gray-700">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>
        </div>
        <div className="relative" ref={dropdownRef}>
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span className="mr-4 text-sm font-medium">{userName}</span>
            <div className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-blue-100 text-blue-600 font-medium">
              <span className="text-sm">{userInitials}</span>
            </div>
          </div>
          
          {/* User dropdown menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700">{userName}</p>
                <p className="text-xs text-gray-500">{userInfo.email}</p>
                <p className="text-xs text-gray-500">{userInfo.role}</p>
              </div>
              <Link to="settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                Account Settings
              </Link>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;